import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';

import { useTownAssets } from '../../stores';
import { appTooltipProps } from '../../utils';
import {
  Column,
  Row,
} from '../display';
import TextInput from '../input/TextInput';
import SvgColorImg from '../SvgColorImg';

// What the note may carry: the API refuses longer, and a picture is not the
// place for a story
const NOTE_MAX = 100;

// A picture before it is sent: what it will look like, turned the right way up,
// and what it says. The turn is applied to the file at send time, so the
// preview shows it the way every later reader will see it.
const PicturePreview = function PicturePreview(props) {
  const {
    file,
    rotation,
    onRotate,
    onRemove,
    note,
    onNote,
  } = props;
  const { assets } = useTownAssets();
  const { translate } = assets;

  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(
    () => () => URL.revokeObjectURL(url),
    [url],
  );

  const onNoteChange = (event) => {
    const { value } = event.currentTarget;
    if (value.length <= NOTE_MAX) onNote(value);
  };

  return (
    <Column gap={2} className="w-full">
      <div className="mx-auto flex h-56 w-56 items-center justify-center overflow-hidden rounded-md bg-pxBgMuted/10">
        <img
          src={url}
          alt={translate('picture')}
          className="h-full w-full object-contain"
          style={{ transform: `rotate(${rotation}deg)` }}
        />
      </div>

      <Row center gap={4}>
        <button
          type="button"
          className="shrink-0 h-9 w-9 flex items-center justify-center"
          onClick={onRotate}
          aria-label={translate('rotate')}
          {...appTooltipProps(translate('rotate'))}
        >
          <SvgColorImg
            src="/images/svg/gui/rotate.svg"
            className="h-6 w-6 fill-pxPrimary"
            imgColorAsCssFill
          />
        </button>

        {(onRemove)
          ? (
              <button
                type="button"
                className="shrink-0 h-9 w-9 flex items-center justify-center"
                onClick={onRemove}
                aria-label={translate('delete')}
                {...appTooltipProps(translate('delete'))}
              >
                <SvgColorImg
                  src="/images/svg/gui/trash.svg"
                  className="h-6 w-6 fill-pxMainButtonCritical"
                  imgColorAsCssFill
                />
              </button>
            )
          : null}
      </Row>

      <TextInput
        id="pictureNote"
        placeholder={translate('notes')}
        value={note}
        onChange={onNoteChange}
        multiline
        hideLabel
        className="w-full bg-white"
      />
    </Column>
  );
};

PicturePreview.propTypes = {
  file: PropTypes.instanceOf(File).isRequired,
  rotation: PropTypes.number.isRequired,
  onRotate: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  note: PropTypes.string.isRequired,
  onNote: PropTypes.func.isRequired,
};

export default PicturePreview;
